import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { DynamicField } from '../../types'; // Updated from ClinicalField
import type { RootState, FieldsState } from '../types';
import { apiSlice } from '../api/apiSlice';
import { initializeApp } from '../thunks/sessionThunks';

// Initial state using the FieldsState interface from types.ts
const initialState: FieldsState = {
  items: [],
  loading: false,
  error: null,
};

const fieldsSlice = createSlice({
  name: 'fields',
  initialState,
  reducers: {
    // Set all fields
    setFields: (state, action: PayloadAction<DynamicField[]>) => {
      state.items = action.payload;
    },
    
    // Clear all fields (useful for logout or reset)
    clearFields: (state) => {
      state.items = [];
    },
    
    // Set loading state
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    
    // Set error state
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    
    // Add or update a single field
    addOrUpdateField: (state, action: PayloadAction<DynamicField>) => {
      const field = action.payload;
      const existingIndex = state.items.findIndex(f => f.field_id === field.field_id);
      
      if (existingIndex >= 0) {
        // Update existing field
        state.items[existingIndex] = field;
      } else {
        // Add new field
        state.items.push(field);
      }
    },
    
    // Remove a field from state
    removeField: (state, action: PayloadAction<string>) => {
      const fieldId = action.payload;
      state.items = state.items.filter(f => f.field_id !== fieldId);
    },
    
    // Update field activity status
    updateFieldStatus: (state, action: PayloadAction<{ fieldId: string; isActive: boolean }>) => {
      const { fieldId, isActive } = action.payload;
      const field = state.items.find(f => f.field_id === fieldId);
      if (field) {
        field.is_active = isActive;
      }
    },
  },
  
  extraReducers: (builder) => {
    // Handle initializeApp thunk
    builder
      .addCase(initializeApp.fulfilled, (state, action) => {
        state.items = action.payload.fields; // Now DynamicField[] instead of ClinicalField[]
        state.loading = false;
        state.error = null;
      })
      .addCase(initializeApp.rejected, (state) => {
        state.loading = false;
        state.error = 'Failed to load fields during initialization';
      });

    // Handle RTK Query getFields
    builder
      .addMatcher(apiSlice.endpoints.getFields.matchPending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addMatcher(apiSlice.endpoints.getFields.matchFulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.items = action.payload; // Now DynamicField[] instead of ClinicalField[]
      })
      .addMatcher(apiSlice.endpoints.getFields.matchRejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to load fields';
      })
      
      // Handle single field retrieval
      .addMatcher(apiSlice.endpoints.getField.matchFulfilled, (state, action) => {
        const field = action.payload; // DynamicField
        const existingIndex = state.items.findIndex(f => f.field_id === field.field_id);
        
        if (existingIndex >= 0) {
          state.items[existingIndex] = field;
        } else {
          state.items.push(field);
        }
      })
      
      // Handle field creation
      .addMatcher(apiSlice.endpoints.createField.matchFulfilled, (state, action) => {
        const newField = action.payload; // DynamicField
        state.items.push(newField);
      })
      
      // Handle field updates
      .addMatcher(apiSlice.endpoints.updateField.matchFulfilled, (state, action) => {
        const updatedField = action.payload; // DynamicField
        const fieldIndex = state.items.findIndex(f => f.field_id === updatedField.field_id);
        
        if (fieldIndex !== -1) {
          state.items[fieldIndex] = updatedField;
        }
      })
      
      // Handle field deletion
      .addMatcher(apiSlice.endpoints.deleteField.matchFulfilled, (state, action) => {
        const fieldId = action.meta.arg.originalArgs; // string
        state.items = state.items.filter(f => f.field_id !== fieldId);
      });
  },
});

// Export actions
export const { 
  setFields, 
  clearFields, 
  setLoading, 
  setError,
  addOrUpdateField,
  removeField,
  updateFieldStatus
} = fieldsSlice.actions;

// Export selectors with updated types
export const selectAllFields = (state: RootState): DynamicField[] => state.fields.items;

export const selectFieldsLoading = (state: RootState): boolean => state.fields.loading;

export const selectFieldsError = (state: RootState): string | null => state.fields.error;

export const selectFieldById = (state: RootState, fieldId: string): DynamicField | undefined =>
  state.fields.items.find(field => field.field_id === fieldId);

// New selectors for field management
export const selectActiveFields = (state: RootState): DynamicField[] =>
  state.fields.items.filter(field => field.is_active);

export const selectFieldByName = (state: RootState, fieldName: string): DynamicField | undefined =>
  state.fields.items.find(field => 
    field.field_name.toLowerCase().includes(fieldName.toLowerCase())
  );

// Selector for fields with prompt instructions
export const selectFieldsWithInstructions = (state: RootState): DynamicField[] =>
  state.fields.items.filter(field => field.prompt_instructions && field.prompt_instructions.length > 0);

// Selector for recently updated fields
export const selectRecentlyUpdatedFields = (state: RootState, limit: number = 5): DynamicField[] => {
  return [...state.fields.items]
    .sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime())
    .slice(0, limit);
};

// Field statistics selector
export const selectFieldStatistics = (state: RootState) => {
  const fields = state.fields.items;
  const totalFields = fields.length;
  const activeFields = fields.filter(f => f.is_active).length;
  const fieldsWithInstructions = fields.filter(f => f.prompt_instructions).length;
  
  return {
    totalFields,
    activeFields,
    inactiveFields: totalFields - activeFields,
    fieldsWithInstructions,
    fieldsWithoutInstructions: totalFields - fieldsWithInstructions,
    averageDescriptionLength: totalFields > 0 
      ? Math.round(fields.reduce((sum, f) => sum + f.description.length, 0) / totalFields)
      : 0
  };
};

export default fieldsSlice.reducer;